import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement, LinearScale, CategoryScale,
  Title, Tooltip, Legend
} from 'chart.js';
import * as Authservices from '../Services/services';

ChartJS.register(BarElement, LinearScale, CategoryScale, Title, Tooltip, Legend);

const TEMPLATES = [
  { id: 'new_episode', name: 'New Episode Alert',    icon: 'fas fa-play-circle', color: '#f82581', title: '🎬 New Episode Available!',    message: "Episode 12 of \"Love in the Shadows\" is now streaming! Don't miss the dramatic twist everyone is talking about." },
  { id: 'vip_promo',   name: 'VIP Promotion',        icon: 'fas fa-crown',       color: '#f59e0b', title: '👑 VIP Flash Sale — 50% OFF!', message: 'Upgrade to VIP for just $4.99/month! Limited time offer ends tonight. Unlock all premium episodes now.' },
  { id: 'coin_bonus',  name: 'Coin Bonus',           icon: 'fas fa-coins',       color: '#10b981', title: '🪙 Double Coin Weekend!',        message: 'Earn 2x coins on all purchases this weekend! Stock up on coins and unlock your favorite episodes.' },
  { id: 'maintenance', name: 'System Maintenance',   icon: 'fas fa-tools',       color: '#6366f1', title: '🔧 Scheduled Maintenance',      message: "We'll be performing system maintenance on Saturday, 2:00 AM - 4:00 AM UTC. The app may be briefly unavailable." },
  { id: 'weekly_picks',name: 'Weekly Picks',         icon: 'fas fa-star',        color: '#8b5cf6', title: '⭐ This Week\'s Top Picks!',     message: 'Check out our curated list of trending series this week. New romance, thriller, and fantasy titles await!' },
  { id: 'custom',      name: 'Custom',               icon: 'fas fa-edit',        color: '#ef4444', title: '',                             message: '' },
];

const AUDIENCE_META = {
  all:      { name: 'All Users',         icon: 'fas fa-globe',      desc: 'Send to all registered users' },
  vip:      { name: 'VIP Only',          icon: 'fas fa-crown',      desc: 'Active VIP subscribers' },
  free:     { name: 'Free Users',        icon: 'fas fa-user',       desc: 'Non-VIP registered users' },
  inactive: { name: 'Inactive (30d+)',   icon: 'fas fa-user-clock', desc: 'No activity in 30+ days' },
  new:      { name: 'New Users (7d)',    icon: 'fas fa-user-plus',  desc: 'Registered in last 7 days' },
};

const TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'Europe/London', 'Europe/Paris', 'Asia/Tokyo',
  'Asia/Shanghai', 'Asia/Kolkata', 'Australia/Sydney',
];

const STATUS_CLASS = { sent: 'sn-status-sent', scheduled: 'sn-status-scheduled', pending: 'sn-status-scheduled', draft: 'sn-status-draft', failed: 'sn-status-failed', cancelled: 'sn-status-draft' };

const SendNotification = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [audience, setAudience] = useState('all');
  const [scheduleMode, setScheduleMode] = useState('now');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [recurring, setRecurring] = useState('once');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const [audienceStats, setAudienceStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPagination, setHistoryPagination] = useState(null);

  const [cancellingId, setCancellingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fileInputRef = useRef(null);

  useEffect(() => {
    Authservices.getNotificationAudienceStats()
      .then(res => {
        if (res?.status === 200) setAudienceStats(res.data?.responseDetails || null);
      })
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  }, []);

  useEffect(() => {
    loadHistory(historyPage);
  }, [historyPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHistory = (page) => {
    setHistoryLoading(true);
    Authservices.getNotificationHistory(page, 20)
      .then(res => {
        if (res?.status === 200) {
          setHistory(res.data?.responseDetails || []);
          setHistoryPagination(res.data?.pagination || null);
        }
      })
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  };

  const audiences = Object.entries(AUDIENCE_META).map(([id, meta]) => ({
    id,
    ...meta,
    reach: audienceStats ? (audienceStats[id] ?? 0) : null,
  }));

  const selectedAudience = audiences.find(a => a.id === audience);

  const selectTemplate = (tpl) => {
    setSelectedTemplate(tpl.id);
    setTitle(tpl.title);
    setMessage(tpl.message);
    setSent(false);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('message', message.trim());
      fd.append('audience', audience);
      if (scheduleMode === 'schedule' && scheduleDate && scheduleTime) {
        fd.append('schedule_at', `${scheduleDate}T${scheduleTime}:00`);
        fd.append('timezone', timezone);
        if (recurring !== 'once') fd.append('recurring', recurring);
      }
      if (image) fd.append('image', image);

      const res = await Authservices.sendNotification(fd);
      if (res?.status === 200 || res?.status === 201) {
        toast.success(res.data?.responseMessage || (scheduleMode === 'schedule' ? 'Notification scheduled!' : 'Notification sent!'));
        setSent(true);
        loadHistory(1);
        setHistoryPage(1);
      }
    } catch (err) {
      toast.error(err?.response?.data?.responseMessage || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const handleCancel = async (id) => {
    setCancellingId(id);
    try {
      const res = await Authservices.cancelNotification(id);
      if (res?.status === 200) {
        toast.success('Notification cancelled');
        loadHistory(historyPage);
      }
    } catch (err) {
      toast.error(err?.response?.data?.responseMessage || 'Failed to cancel notification');
    } finally {
      setCancellingId(null);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      const res = await Authservices.deleteNotification(id);
      if (res?.status === 200) {
        toast.success('Notification deleted');
        setHistory(prev => prev.filter(n => n.id !== id));
      }
    } catch (err) {
      toast.error(err?.response?.data?.responseMessage || 'Failed to delete notification');
    } finally {
      setDeletingId(null);
    }
  };

  const handleReset = () => {
    setSelectedTemplate(null); setTitle(''); setMessage(''); setAudience('all');
    setScheduleMode('now'); setScheduleDate(''); setScheduleTime('');
    setTimezone('UTC'); setRecurring('once'); setImage(null); setImagePreview(null);
    setSent(false);
  };

  const sentCount  = history.filter(n => n.status === 'sent').length;
  const avgOpen    = history.filter(n => n.open_rate_pct != null).reduce((s, n, _, a) => s + n.open_rate_pct / a.length, 0);
  const avgClick   = history.filter(n => n.click_rate_pct != null).reduce((s, n, _, a) => s + n.click_rate_pct / a.length, 0);
  const scheduled  = history.filter(n => n.status === 'scheduled' || n.status === 'pending').length;

  const historyChartData = {
    labels: history.slice(-7).map(n => n.title?.slice(0, 12) + '…'),
    datasets: [
      { label: 'Open Rate %',  data: history.slice(-7).map(n => n.open_rate_pct  ?? 0), backgroundColor: 'rgba(16, 185, 129, 0.6)',  borderRadius: 4, borderSkipped: false },
      { label: 'Click Rate %', data: history.slice(-7).map(n => n.click_rate_pct ?? 0), backgroundColor: 'rgba(99, 102, 241, 0.6)',  borderRadius: 4, borderSkipped: false },
    ],
  };

  const chartOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'top', labels: { color: 'rgba(255,255,255,0.6)', font: { size: 11 }, boxWidth: 12, padding: 16 } },
      tooltip: { backgroundColor: 'rgba(18,18,30,0.95)', borderColor: 'rgba(255,255,255,0.1)', borderWidth: 1, padding: 12, cornerRadius: 8, titleColor: '#fff', bodyColor: 'rgba(255,255,255,0.8)' },
    },
    scales: {
      x: { ticks: { color: 'rgba(255,255,255,0.4)', font: { size: 11 } }, grid: { display: false }, border: { display: false } },
      y: { ticks: { color: 'rgba(255,255,255,0.4)', font: { size: 11 } }, grid: { color: 'rgba(255,255,255,0.04)' }, border: { display: false } },
    },
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  return (
    <div className="sn-container">
      <div className="sn-header">
        <div className="sn-header-left">
          <h4>Send Notification</h4>
          <p className="sn-subtitle">Create and schedule push notifications for your users</p>
        </div>
        <div className="sn-header-right">
          <button className="sn-btn-outline" onClick={handleReset}><i className="fas fa-redo"></i> Reset</button>
        </div>
      </div>

      <h6 className="sn-section-label"><i className="fas fa-layer-group"></i> Notification Templates</h6>
      <div className="sn-templates-grid">
        {TEMPLATES.map(tpl => (
          <div key={tpl.id} className={`sn-template-card ${selectedTemplate === tpl.id ? 'sn-template-active' : ''}`} onClick={() => selectTemplate(tpl)}>
            <div className="sn-template-icon" style={{ background: tpl.color + '18', color: tpl.color }}><i className={tpl.icon}></i></div>
            <div className="sn-template-name">{tpl.name}</div>
            {selectedTemplate === tpl.id && <div className="sn-template-check"><i className="fas fa-check-circle"></i></div>}
          </div>
        ))}
      </div>

      <div className="sn-main-grid">
        <div className="sn-form-col">
          <div className="sn-card">
            <h6 className="sn-card-title"><i className="fas fa-pencil-alt"></i> Notification Content</h6>
            <div className="sn-form-group">
              <label>Title</label>
              <input type="text" className="sn-input" placeholder="Enter notification title..." value={title} onChange={e => { setTitle(e.target.value); setSent(false); }} />
            </div>
            <div className="sn-form-group">
              <label>Message</label>
              <textarea className="sn-textarea" placeholder="Enter notification message..." rows={4} value={message} onChange={e => { setMessage(e.target.value); setSent(false); }} />
              <div className="sn-char-count">{message.length}/500</div>
            </div>
            <div className="sn-form-group">
              <label>Image <span style={{ color: '#8a8a9a', fontWeight: 400 }}>(optional — JPEG, PNG, GIF, WEBP)</span></label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button className="sn-btn-outline" style={{ padding: '6px 14px', fontSize: 13 }} onClick={() => fileInputRef.current?.click()}>
                  <i className="fas fa-image"></i> {image ? 'Change Image' : 'Upload Image'}
                </button>
                {imagePreview && (
                  <div style={{ position: 'relative' }}>
                    <img src={imagePreview} alt="preview" style={{ height: 40, borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)' }} />
                    <button onClick={() => { setImage(null); setImagePreview(null); }} style={{ position: 'absolute', top: -6, right: -6, background: '#ef4444', border: 'none', borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', color: '#fff', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><i className="fas fa-times"></i></button>
                  </div>
                )}
                {image && !imagePreview && <span style={{ fontSize: 12, color: '#8a8a9a' }}>{image.name}</span>}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageChange} />
            </div>
          </div>

          <div className="sn-card">
            <h6 className="sn-card-title"><i className="fas fa-users"></i> Audience Targeting {statsLoading && <span style={{ fontSize: 11, color: '#8a8a9a', marginLeft: 8 }}>Loading…</span>}</h6>
            <div className="sn-audience-grid">
              {audiences.map(aud => (
                <div key={aud.id} className={`sn-audience-card ${audience === aud.id ? 'sn-audience-active' : ''}`} onClick={() => setAudience(aud.id)}>
                  <div className="sn-audience-icon"><i className={aud.icon}></i></div>
                  <div className="sn-audience-info">
                    <div className="sn-audience-name">{aud.name}</div>
                    <div className="sn-audience-desc">{aud.desc}</div>
                  </div>
                  {audience === aud.id && <div className="sn-audience-check"><i className="fas fa-check"></i></div>}
                </div>
              ))}
            </div>
            <div className="sn-reach-box">
              <i className="fas fa-signal"></i>
              <span>Estimated Reach:</span>
              <strong>
                {statsLoading ? '…' : selectedAudience?.reach != null ? selectedAudience.reach.toLocaleString() + ' users' : '—'}
              </strong>
            </div>
          </div>

          <div className="sn-card">
            <h6 className="sn-card-title"><i className="fas fa-clock"></i> Scheduling</h6>
            <div className="sn-schedule-toggle">
              <button className={`sn-toggle-btn ${scheduleMode === 'now' ? 'sn-toggle-active' : ''}`} onClick={() => setScheduleMode('now')}><i className="fas fa-paper-plane"></i> Send Now</button>
              <button className={`sn-toggle-btn ${scheduleMode === 'schedule' ? 'sn-toggle-active' : ''}`} onClick={() => setScheduleMode('schedule')}><i className="fas fa-calendar-alt"></i> Schedule</button>
            </div>
            {scheduleMode === 'schedule' && (
              <div className="sn-schedule-fields">
                <div className="sn-schedule-row">
                  <div className="sn-form-group sn-half">
                    <label>Date</label>
                    <input type="date" className="sn-input" value={scheduleDate} onChange={e => setScheduleDate(e.target.value)} />
                  </div>
                  <div className="sn-form-group sn-half">
                    <label>Time</label>
                    <input type="time" className="sn-input" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)} />
                  </div>
                </div>
                <div className="sn-schedule-row">
                  <div className="sn-form-group sn-half">
                    <label>Timezone</label>
                    <select className="sn-input" value={timezone} onChange={e => setTimezone(e.target.value)}>
                      {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                    </select>
                  </div>
                  <div className="sn-form-group sn-half">
                    <label>Recurring</label>
                    <select className="sn-input" value={recurring} onChange={e => setRecurring(e.target.value)}>
                      <option value="once">Once</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="sn-actions">
            <button className="sn-btn-primary" disabled={!title.trim() || !message.trim() || sending} onClick={handleSend}>
              {sending ? <><div className="sn-spinner"></div> Sending…</> : sent ? <><i className="fas fa-check"></i> {scheduleMode === 'schedule' ? 'Scheduled!' : 'Sent!'}</> : <><i className="fas fa-paper-plane"></i> {scheduleMode === 'schedule' ? 'Schedule Notification' : 'Send Notification'}</>}
            </button>
          </div>
        </div>

        <div className="sn-preview-col">
          <div className="sn-card sn-preview-card">
            <h6 className="sn-card-title"><i className="fas fa-mobile-alt"></i> Live Preview</h6>
            <div className="sn-phone-mockup">
              <div className="sn-phone-frame">
                <div className="sn-phone-notch"></div>
                <div className="sn-phone-status-bar">
                  <span>9:41</span>
                  <div className="sn-phone-status-icons"><i className="fas fa-signal"></i><i className="fas fa-wifi"></i><i className="fas fa-battery-full"></i></div>
                </div>
                <div className="sn-phone-content">
                  <div className="sn-phone-notification">
                    <div className="sn-notif-header">
                      <div className="sn-notif-app-icon"><i className="fas fa-play-circle"></i></div>
                      <div className="sn-notif-app-info">
                        <span className="sn-notif-app-name">HikeCinema</span>
                        <span className="sn-notif-time">now</span>
                      </div>
                    </div>
                    <div className="sn-notif-body" style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="sn-notif-title">{title || 'Notification Title'}</div>
                        <div className="sn-notif-message">{message || 'Your notification message will appear here…'}</div>
                      </div>
                      {imagePreview && (
                        <img
                          src={imagePreview}
                          alt=""
                          style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', flexShrink: 0, border: '1px solid rgba(255,255,255,0.1)' }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="sn-phone-wallpaper">
                    <div className="sn-wallpaper-time">9:41</div>
                    <div className="sn-wallpaper-date">Wednesday, February 19</div>
                  </div>
                </div>
                <div className="sn-phone-home-bar"></div>
              </div>
            </div>
          </div>

          <div className="sn-card">
            <h6 className="sn-card-title"><i className="fas fa-info-circle"></i> Summary</h6>
            <div className="sn-summary-list">
              <div className="sn-summary-item"><span className="sn-summary-label">Template</span><span className="sn-summary-value">{TEMPLATES.find(t => t.id === selectedTemplate)?.name || 'None'}</span></div>
              <div className="sn-summary-item"><span className="sn-summary-label">Audience</span><span className="sn-summary-value">{selectedAudience?.name}</span></div>
              <div className="sn-summary-item"><span className="sn-summary-label">Reach</span><span className="sn-summary-value">{statsLoading ? '…' : selectedAudience?.reach != null ? selectedAudience.reach.toLocaleString() : '—'}</span></div>
              <div className="sn-summary-item"><span className="sn-summary-label">Delivery</span><span className="sn-summary-value">{scheduleMode === 'now' ? 'Immediate' : `${scheduleDate || '—'} ${scheduleTime || ''} ${timezone}`}</span></div>
              <div className="sn-summary-item"><span className="sn-summary-label">Image</span><span className="sn-summary-value">{image ? image.name : 'None'}</span></div>
            </div>
          </div>
        </div>
      </div>

      <div className="sn-history-section">
        <div className="sn-history-header">
          <h6 className="sn-card-title"><i className="fas fa-history"></i> Notification History {historyLoading && <span style={{ fontSize: 11, color: '#8a8a9a', marginLeft: 8 }}>Loading…</span>}</h6>
        </div>

        {history.length > 0 && (
          <div className="sn-history-chart-row">
            <div className="sn-card sn-history-chart-card">
              <h6 className="sn-card-title"><i className="fas fa-chart-bar"></i> Recent Open & Click Rates</h6>
              <div className="sn-chart-body"><Bar data={historyChartData} options={chartOpts} /></div>
            </div>
            <div className="sn-card sn-history-stats-card">
              <div className="sn-history-stat">
                <div className="sn-history-stat-icon" style={{ background: 'rgba(248,37,129,0.15)', color: '#f82581' }}><i className="fas fa-paper-plane"></i></div>
                <div className="sn-history-stat-info"><div className="sn-history-stat-value">{historyPagination?.total ?? sentCount}</div><div className="sn-history-stat-label">Total Sent</div></div>
              </div>
              <div className="sn-history-stat">
                <div className="sn-history-stat-icon" style={{ background: 'rgba(16,185,129,0.15)', color: '#10b981' }}><i className="fas fa-envelope-open"></i></div>
                <div className="sn-history-stat-info"><div className="sn-history-stat-value">{avgOpen > 0 ? `${avgOpen.toFixed(1)}%` : '—'}</div><div className="sn-history-stat-label">Avg Open Rate</div></div>
              </div>
              <div className="sn-history-stat">
                <div className="sn-history-stat-icon" style={{ background: 'rgba(99,102,241,0.15)', color: '#6366f1' }}><i className="fas fa-mouse-pointer"></i></div>
                <div className="sn-history-stat-info"><div className="sn-history-stat-value">{avgClick > 0 ? `${avgClick.toFixed(1)}%` : '—'}</div><div className="sn-history-stat-label">Avg Click Rate</div></div>
              </div>
              <div className="sn-history-stat">
                <div className="sn-history-stat-icon" style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b' }}><i className="fas fa-calendar-check"></i></div>
                <div className="sn-history-stat-info"><div className="sn-history-stat-value">{scheduled}</div><div className="sn-history-stat-label">Scheduled</div></div>
              </div>
            </div>
          </div>
        )}

        <div className="sn-card">
          {historyLoading && history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#8a8a9a' }}><i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }}></i><p style={{ marginTop: 12 }}>Loading history…</p></div>
          ) : history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#8a8a9a' }}><i className="fas fa-bell-slash" style={{ fontSize: 32, opacity: 0.4 }}></i><p style={{ marginTop: 12 }}>No notifications sent yet</p></div>
          ) : (
            <div className="sn-table-wrap">
              <table className="sn-history-table">
                <thead>
                  <tr><th>Title</th><th>Audience</th><th>Status</th><th>Sent / Scheduled</th><th>Reach</th><th>Open Rate</th><th>Click Rate</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {history.map(item => (
                    <tr key={item.id} className="sn-table-row">
                      <td className="sn-table-title">{item.title}</td>
                      <td className="sn-table-audience" style={{ textTransform: 'capitalize' }}>{AUDIENCE_META[item.audience]?.name || item.audience}</td>
                      <td><span className={`sn-status-badge ${STATUS_CLASS[item.status] || ''}`} style={{ textTransform: 'capitalize' }}>{item.status}</span></td>
                      <td className="sn-table-date">{formatDate(item.sent_at || item.schedule_at)}</td>
                      <td>{item.sent_count != null ? Number(item.sent_count).toLocaleString() : '—'}</td>
                      <td className="sn-table-rate">
                        {item.open_rate_pct != null ? (
                          <div className="sn-rate-cell"><div className="sn-rate-bar-wrap"><div className="sn-rate-bar" style={{ width: `${item.open_rate_pct}%`, background: '#10b981' }}></div></div><span>{item.open_rate_pct}%</span></div>
                        ) : '—'}
                      </td>
                      <td className="sn-table-rate">
                        {item.click_rate_pct != null ? (
                          <div className="sn-rate-cell"><div className="sn-rate-bar-wrap"><div className="sn-rate-bar" style={{ width: `${item.click_rate_pct}%`, background: '#6366f1' }}></div></div><span>{item.click_rate_pct}%</span></div>
                        ) : '—'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {(item.status === 'scheduled' || item.status === 'pending') && (
                            <button
                              onClick={() => handleCancel(item.id)}
                              disabled={cancellingId === item.id}
                              title="Cancel"
                              style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 12 }}
                            >
                              {cancellingId === item.id ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-ban"></i>}
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(item.id)}
                            disabled={deletingId === item.id}
                            title="Delete"
                            style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 12 }}
                          >
                            {deletingId === item.id ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-trash"></i>}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {historyPagination && historyPagination.pages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '16px 0 4px' }}>
              <button onClick={() => setHistoryPage(p => Math.max(1, p - 1))} disabled={historyPage === 1} className="sn-btn-outline" style={{ padding: '4px 12px' }}>← Prev</button>
              <span style={{ color: '#8a8a9a', lineHeight: '32px', fontSize: 13 }}>Page {historyPage} of {historyPagination.pages}</span>
              <button onClick={() => setHistoryPage(p => Math.min(historyPagination.pages, p + 1))} disabled={historyPage === historyPagination.pages} className="sn-btn-outline" style={{ padding: '4px 12px' }}>Next →</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SendNotification;
